import React, { useRef, useEffect, useState } from 'react'
import './index.scss'
import { connect } from 'react-redux'
import * as maptilersdk from '@maptiler/sdk'
import '@maptiler/sdk/dist/maptiler-sdk.css'
import { setMapReferenceThreeD } from '../../action/map-reference-actions'
import { userSettingsSet, userSettingsUpdateRequest } from '../../action/user-settings-actions'
import Render3dMarkers from './render-3d-markers'
import Render3dPhotos from './render-3d-photos'
import Render3dLine from './render-3d-line'
import Render3dPolygons from './render-3d-polygons'
import Render3dTracks from './render-3d-tracks'
import RenderCircles3D from './render-3d-circle'
import Render3dLayer from './render-3d-layer'
import { debounce } from '../hunt-planner/huntplanner-layer-render/map-functions'
import { ensureSession, getCurrentToken, clearSession } from './google-tile-session'
import { registerTileGuardProtocol } from './tile-request-guard'
import TerrainAnalysis from '../terrain-analysis'
import Viewshed from '../viewshed'
import CheckWind from '../check-wind'
import MapControls3D from './map-controls-3d'
import { topoLinesLayerIdForZoom, TOPO_LINES_MIN_ZOOM } from '../../lib/topoLineLayers'
import { nextActiveLayerIds } from '../../lib/basemapLayerSwap'
import { TERRARIUM_DEM_SOURCE } from './terrarium-dem-source'

// Increase tile-decode workers from the default of 1 before any Map is created.
// Must be module-level so it runs before the first Map() constructor call.
maptilersdk.setWorkerCount(Math.min(navigator.hardwareConcurrency || 4, 4))

// Register the raster tile guard protocol before any map is created.
// This prevents MapLibre from retrying 4xx tile failures on every render frame.
registerTileGuardProtocol()

// Concurrency limiter for the google-sat protocol. Without this, a large tile burst
// saturates Chrome's H3 stream queue for tile.googleapis.com, causing 20-28s blocked
// times and mass cancellations when the user pans.
const GOOGLE_SAT_MAX_CONCURRENT = 8
let _googleSatActive = 0
const _googleSatQueue = []
function _drainGoogleSatQueue() {
  while (_googleSatActive < GOOGLE_SAT_MAX_CONCURRENT && _googleSatQueue.length > 0) {
    _googleSatQueue.shift()()
  }
}

class PitchBearingControl {
  onAdd(map) {
    this._map = map
    this._el = document.createElement('div')
    this._el.className = 'maplibregl-ctrl maplibregl-ctrl-group'
    this._el.style.cssText = 'padding:2px 0'

    const tiltUpSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18"><polygon points="10,4 3,16 17,16" fill="currentColor"/><rect x="3" y="17" width="14" height="2" rx="1" fill="currentColor"/></svg>'
    const tiltDownSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18"><rect x="3" y="1" width="14" height="2" rx="1" fill="currentColor"/><polygon points="10,16 3,4 17,4" fill="currentColor"/></svg>'
    const rotLeftSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4.5 10a5.5 5.5 0 1 0 5.5-5.5"/><polyline points="6,2 4,6 8,6" fill="currentColor" stroke="none"/></svg>'
    const rotRightSvg = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M15.5 10a5.5 5.5 0 1 1-5.5-5.5"/><polyline points="14,2 16,6 12,6" fill="currentColor" stroke="none"/></svg>'

    const makeBtn = (svg, title) => {
      const b = document.createElement('button')
      b.type = 'button'
      b.title = title
      b.setAttribute('aria-label', title)
      b.style.cssText = 'display:flex;align-items:center;justify-content:center;color:#333;width:29px;height:29px'
      b.innerHTML = svg
      return b
    }

    const makeLabel = (text) => {
      const s = document.createElement('div')
      s.style.cssText = 'font-size:9px;color:#555;text-align:center;line-height:1;padding:1px 0 3px'
      s.textContent = text
      return s
    }

    this._tiltUpBtn = makeBtn(tiltUpSvg, 'Tilt up')
    this._tiltDownBtn = makeBtn(tiltDownSvg, 'Tilt down')
    this._tiltLabel = makeLabel('Tilt 0deg')

    this._tiltUpBtn.addEventListener('click', () => {
      const z = map.getZoom()
      const limit = z < 4 ? 0 : z < 6 ? 20 : z < 8 ? 45 : z < 10 ? 60 : 85
      map.easeTo({ pitch: Math.min(map.getPitch() + 10, limit), duration: 300 })
    })
    this._tiltDownBtn.addEventListener('click', () => {
      map.easeTo({ pitch: Math.max(map.getPitch() - 10, 0), duration: 300 })
    })

    this._rotLeftBtn = makeBtn(rotLeftSvg, 'Rotate left')
    this._rotRightBtn = makeBtn(rotRightSvg, 'Rotate right')
    this._bearingLabel = makeLabel('Rot 0deg')

    this._rotLeftBtn.addEventListener('click', () => { const b = map.getBearing(); map.easeTo({ bearing: Math.round(b / 30) * 30 - 30, duration: 300 }) })
    this._rotRightBtn.addEventListener('click', () => { const b = map.getBearing(); map.easeTo({ bearing: Math.round(b / 30) * 30 + 30, duration: 300 }) })

    this._el.appendChild(this._tiltUpBtn)
    this._el.appendChild(this._tiltDownBtn)
    this._el.appendChild(this._tiltLabel)
    this._el.appendChild(this._rotLeftBtn)
    this._el.appendChild(this._rotRightBtn)
    this._el.appendChild(this._bearingLabel)

    const update = () => {
      const z = map.getZoom()
      const tiltLimit = z < 4 ? 0 : z < 6 ? 20 : z < 8 ? 45 : z < 10 ? 60 : 85
      const disabled = tiltLimit === 0
      this._tiltUpBtn.disabled = disabled
      this._tiltUpBtn.style.opacity = disabled ? '0.3' : '1'
      this._tiltUpBtn.title = disabled ? 'Zoom in to enable tilt' : 'Tilt up'
      this._tiltLabel.textContent = 'Tilt ' + Math.round(map.getPitch()) + 'deg'
      this._bearingLabel.textContent = 'Rot ' + Math.round(((map.getBearing() % 360) + 360) % 360) + 'deg'
    }

    map.on('pitch', update)
    map.on('rotate', update)
    map.on('zoom', update)
    map.on('load', update)
    setTimeout(update, 500)

    return this._el
  }

  onRemove() {
    this._el.parentNode?.removeChild(this._el)
    this._map = null
  }
}

const BASEMAPS = [
  { id: 'satellite', label: 'SAT',  googleSat: true },
  { id: 'hybrid',    label: 'HYB',  style: maptilersdk.MapStyle.HYBRID },
  { id: 'topo',      label: 'TOPO', style: maptilersdk.MapStyle.TOPO },
]

// Controls which provider supplies the base satellite/hybrid imagery.
// 'maptiler' — MapTiler satellite-v2 raster tiles (no Google API key required)
// 'google'   — Google Maps Tile API (requires active session token)
// Future: make this a user-facing control once additional providers are added.
const BASE_IMAGERY_PROVIDER = 'maptiler'

// Converts XYZ tile coordinates to a Web Mercator bbox string (west,south,east,north in meters)
// matching the format expected by the layer render API.
function tileToBbox(x, y, z) {
  const size = 20037508.34 * 2 / Math.pow(2, z)
  const west  = -20037508.34 + x * size
  const east  = -20037508.34 + (x + 1) * size
  const north =  20037508.34 - y * size
  const south =  20037508.34 - (y + 1) * size
  return `${west},${south},${east},${north}`
}

function removeContourOverlay(map) {
  try {
    if (map.getLayer('contours-lines')) map.removeLayer('contours-lines')
    if (map.getSource('contours-src')) map.removeSource('contours-src')
  } catch (_) {}
}

function addContourOverlay(map) {
  try {
    if (map.getSource('contours-src')) return
    map.addSource('contours-src', {
      type: 'raster',
      tiles: ['basemap-topo://{z}/{x}/{y}'],
      tileSize: 256,
      // Topo lines are not served below z11, so don't ask for tiles the protocol
      // handler would only reject.
      minzoom: TOPO_LINES_MIN_ZOOM,
      maxzoom: 18,
    })
    map.addLayer({
      id: 'contours-lines',
      type: 'raster',
      source: 'contours-src',
      paint: { 'raster-opacity': 0.9, 'raster-fade-duration': 0 },
    })
  } catch (_) {}
}

// The HYB basemap uses the same Google imagery as SAT — satellite tiles with the
// road + label overlay baked in (the google-sat session is created with the
// 'layerRoadmap' layer type, the modern equivalent of the legacy `lyrs=y`). The
// only difference is the topographic contour overlay, which handleBasemapChange
// adds on top once the style finishes loading.
function makeGoogleHybridStyle(maptilerApiKey) {
  return makeGoogleSatelliteStyle(maptilerApiKey)
}

function makeMapTilerSatelliteStyle() {
  return {
    version: 8,
    sources: {
      'maptiler-sat-src': {
        type: 'raster',
        tiles: [`https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${__MAPTILER_API_KEY__}`],
        tileSize: 256,
        attribution: '© MapTiler © OpenStreetMap contributors',
        minzoom: 0,
        maxzoom: 20,
      },
      'terrain-dem': TERRARIUM_DEM_SOURCE,
    },
    terrain: { source: 'terrain-dem', exaggeration: 1 },
    layers: [{
      id: 'maptiler-satellite',
      type: 'raster',
      source: 'maptiler-sat-src',
    }],
  }
}

function makeGoogleSatelliteStyle(maptilerApiKey) {
  return {
    version: 8,
    sources: {
      'google-sat-src': {
        type: 'raster',
        tiles: ['google-sat://{z}/{x}/{y}'],
        tileSize: 256,
        attribution: '© Google',
        minzoom: 0,
        maxzoom: 20,
      },
      'terrain-dem': TERRARIUM_DEM_SOURCE,
    },
    terrain: { source: 'terrain-dem', exaggeration: 1 },
    layers: [{
      id: 'google-satellite',
      type: 'raster',
      source: 'google-sat-src',
    }],
  }
}

const MapTiler3D = ({ googleMap, setMapReferenceThreeD, load3dMap, token, selectedTopo, layersDictionary, userSettings, userSettingsSet, userSettingsUpdateRequest }) => {
  const mapContainer = useRef(null)
  const [mapInstance, setMapInstance] = useState(null)
  const [activeBasemap, setActiveBasemap] = useState('satellite')
  const [styleKey, setStyleKey] = useState(0)
  const styleKeyRef = useRef(0)
  const [exaggeration, setExaggeration] = useState(1)
  const tokenRef = useRef(token)
  // Holds the full layer object so the user-topo protocol handler has the
  // correct tile server URL — same data imageryAndTopographyOthers uses in 2D.
  const selectedTopoLayerRef = useRef(null)
  const pendingStyleLoadRef = useRef(null)
  const pendingContourRef = useRef(null)
  useEffect(() => { tokenRef.current = token }, [token])
  useEffect(() => {
    selectedTopoLayerRef.current = (layersDictionary && selectedTopo)
      ? (layersDictionary[selectedTopo] ?? null)
      : null
  }, [selectedTopo, layersDictionary])

  // When the user picks a TOPO layer from the layer menu, auto-switch the 3D
  // basemap to TOPO (mirroring 2D behaviour). Skip the initial mount run.
  const topoAutoSwitchRef = useRef(false)
  useEffect(() => {
    if (!topoAutoSwitchRef.current) { topoAutoSwitchRef.current = true; return }
    if (!mapInstance) return
    handleBasemapChange(BASEMAPS.find(b => b.id === 'topo'))
  }, [selectedTopo])
  useEffect(() => {
    if (!mapInstance) return
    try {
      const terrain = mapInstance.getTerrain()
      if (terrain) mapInstance.setTerrain({ source: terrain.source, exaggeration })
    } catch (_) {}
  }, [exaggeration, mapInstance])

  // Mirrors setBaseLayer from layer-toggle: replaces the active basemap in userSettings
  // so the 2D layer panel stays in sync when the user changes basemap in 3D.
  const syncBasemapToUserSettings = (basemapId) => {
    const newLayerId = basemapId === 'hybrid' ? 891 : basemapId === 'satellite' ? 81 : selectedTopo
    if (!newLayerId || !layersDictionary || !userSettings) return
    const settingClone = userSettings.map(s => Object.assign({}, s))
    const active = settingClone.find(s => s.settingId === 1)
    if (!active) return
    const activeArr = active.valueArray ?? []
    const activeBM = activeArr.find(id => layersDictionary[id]?.layerNodeId === 5)
    if (activeBM === newLayerId) return
    const newActive = nextActiveLayerIds({
      activeArr,
      layersDictionary,
      newLayerId,
      replaceAllBasemaps: basemapId === 'satellite' || basemapId === 'hybrid'
    })
    if (!newActive) return
    active.value = JSON.stringify(newActive)
    active.valueArray = newActive
    userSettingsSet(settingClone)
    userSettingsUpdateRequest(1, JSON.stringify(newActive), false)
  }

  const handleBasemapChange = (basemap) => {
    if (!mapInstance) return
    const isAlreadyActive = activeBasemap === basemap.id
    // For TOPO: always re-apply so that switching between topo layers (USGS → ESRI etc.)
    // picks up the new selectedTopoLayerRef even when the basemap is already 'topo'.
    if (isAlreadyActive && basemap.id !== 'topo') return
    if (!isAlreadyActive) {
      setActiveBasemap(basemap.id)
      // Cancel any in-flight style.load callback from a previous switch. Without
      // this, a rapid SAT→HYB→SAT sequence leaves a stale HYB style.load handler
      // that fires on the next setStyle call and re-adds contours to the SAT map.
      if (pendingStyleLoadRef.current) {
        mapInstance.off('style.load', pendingStyleLoadRef.current)
        pendingStyleLoadRef.current = null
      }
      if (pendingContourRef.current) {
        mapInstance.off('idle', pendingContourRef.current)
        pendingContourRef.current = null
      }
      removeContourOverlay(mapInstance)
    }

    syncBasemapToUserSettings(basemap.id)

    const applyStyle = (style) => {
      if (!style) {
        if (__DEBUG__) console.warn('[MapTiler3D] setStyle skipped — falsy style payload:', style)
        return
      }
      if (typeof style === 'object' &&
          Object.prototype.hasOwnProperty.call(style, 'version') &&
          style.version !== 8) {
        if (__DEBUG__) console.warn('[MapTiler3D] setStyle skipped — style.version !== 8:', style)
        return
      }
      mapInstance.setStyle(style)
      const onStyleLoad = () => {
        pendingStyleLoadRef.current = null
        try {
          // Satellite and hybrid bake terrain-dem into their style JSON; topo has
          // neither — addSource is a no-op when the source already exists so one
          // path handles all three cases.
          if (!mapInstance.getSource('terrain-dem')) {
            mapInstance.addSource('terrain-dem', TERRARIUM_DEM_SOURCE)
          }
          mapInstance.setTerrain({ source: 'terrain-dem', exaggeration })
        } catch (_) {}
        // Defer component remount until idle — SAT style loads synchronously
        // from a JS object with cached DEM tiles, so idle fires before React's
        // effects can register their own once('idle') listeners in Render3dLayer.
        // Waiting here guarantees isStyleLoaded() is true when effects run.
        mapInstance.once('idle', () => {
          styleKeyRef.current += 1
          setStyleKey(styleKeyRef.current)
        })
        if (basemap.id === 'hybrid') {
          // Wait for idle so contours render above the imagery layers.
          // Store the callback ref so it can be cancelled if the user switches
          // away before idle fires — otherwise the stale callback would add
          // contours on top of the next basemap (SAT/TOPO).
          const addContour = () => {
            pendingContourRef.current = null
            addContourOverlay(mapInstance)
          }
          pendingContourRef.current = addContour
          mapInstance.once('idle', addContour)
        }
      }
      pendingStyleLoadRef.current = onStyleLoad
      mapInstance.once('style.load', onStyleLoad)
    }

    if (basemap.id === 'satellite') {
      applyStyle(BASE_IMAGERY_PROVIDER === 'maptiler'
        ? makeMapTilerSatelliteStyle()
        : makeGoogleSatelliteStyle(maptilersdk.config.apiKey))
    } else if (basemap.id === 'hybrid') {
      applyStyle(BASE_IMAGERY_PROVIDER === 'maptiler'
        ? makeMapTilerSatelliteStyle()
        : makeGoogleHybridStyle(maptilersdk.config.apiKey))
    } else if (basemap.id === 'topo') {
      // Use the user-selected topo layer if it has a tile server (ESRI/USGS/Mapbox).
      // Falls back to MapTiler TOPO style for MapTiler-based layers (TOPO Outdoors etc.)
      const topoLayer = selectedTopoLayerRef.current
      console.log('[3D TOPO] layer:', JSON.stringify(topoLayer && {id:topoLayer.id, name:topoLayer.name, server:topoLayer.server, serverLayerName:topoLayer.serverLayerName, cloudfrontUrl:topoLayer.cloudfrontUrl, layerNodeId:topoLayer.layerNodeId}))
      // Google Terrain (id=82) stores "<required>" as a placeholder — treat as no server.
      const hasServer = topoLayer?.server && topoLayer.server !== '<required>'
      if (topoLayer && (hasServer || topoLayer.cloudfrontUrl || (topoLayer.id >= 7000 && topoLayer.serverLayerName))) {
        // Embed the layer ID in the tile URL so MapLibre's _diffStyle sees a different
        // source when switching between topo layers (e.g. USGS→ESRI). Without this,
        // both produce identical style objects and MapLibre skips re-requesting tiles.
        applyStyle({
          version: 8,
          sources: {
            'user-topo-src': { type: 'raster', tiles: [`user-topo://${topoLayer.id}/{z}/{x}/{y}`], tileSize: 256, minzoom: 3, maxzoom: 18 },
            'terrain-dem': TERRARIUM_DEM_SOURCE,
          },
          terrain: { source: 'terrain-dem', exaggeration: 1 },
          layers: [{ id: 'user-topo-layer', type: 'raster', source: 'user-topo-src' }],
        })
      } else {
        // MapTiler-based or unknown layer — MapTiler TOPO style is the best match
        applyStyle(maptilersdk.MapStyle.TOPO)
      }
    } else {
      applyStyle(basemap.style)
    }
  }

  // On first mount, sync the 3D basemap to whatever was active in 2D.
  // The map always initializes as satellite, so we need to switch after load.
  const initialBasemapSynced = useRef(false)
  useEffect(() => {
    if (!mapInstance || initialBasemapSynced.current) return
    initialBasemapSynced.current = true
    const setting = userSettings?.find(s => s.settingId === 1)
    const arr = setting?.valueArray ?? []
    const activeBM2d = arr.find(id => layersDictionary?.[id]?.layerNodeId === 5)
    let target = 'satellite'
    if (activeBM2d === 891) target = 'hybrid'
    else if (activeBM2d && activeBM2d !== 81) target = 'topo'
    if (target !== 'satellite') {
      handleBasemapChange(BASEMAPS.find(b => b.id === target))
    }
  }, [mapInstance])

  useEffect(() => {
    if (!mapContainer.current) return

    maptilersdk.config.apiKey = __MAPTILER_API_KEY__

    // Pre-warm the Google Tile session so the token is ready before the first tile request.
    if (BASE_IMAGERY_PROVIDER === 'google') ensureSession()

    // Register the Google satellite tile protocol with a concurrency limiter.
    // Without the limit, large tile bursts saturate Chrome's H3 stream queue for
    // tile.googleapis.com, causing 20-28s blocked times and mass cancellations.
    _googleSatActive = 0
    _googleSatQueue.length = 0
    maptilersdk.addProtocol('google-sat', (params, abortController) =>
      new Promise((resolve, reject) => {
        const execute = async () => {
          if (abortController.signal.aborted) {
            reject(new DOMException('Aborted', 'AbortError'))
            _drainGoogleSatQueue()
            return
          }
          _googleSatActive++
          try {
            const [z, x, y] = params.url.replace('google-sat://', '').split('/').map(Number)
            const token = getCurrentToken() || await ensureSession()
            if (!token) throw new Error('No Google Tile session token')
            const url = `https://tile.googleapis.com/v1/2dtiles/${z}/${x}/${y}` +
                        `?session=${token}&key=${__GOOGLE_MAPS_KEY__}`
            const res = await fetch(url, { signal: abortController.signal })
            if (!res.ok) throw new Error(`Google satellite tile HTTP ${res.status}`)
            resolve({ data: await res.arrayBuffer() })
          } catch (e) {
            reject(e)
          } finally {
            _googleSatActive--
            _drainGoogleSatQueue()
          }
        }
        if (_googleSatActive < GOOGLE_SAT_MAX_CONCURRENT) execute()
        else _googleSatQueue.push(execute)
      })
    )

    // Register a custom protocol so the contour raster source can fetch tiles
    // from the app's authenticated render API without touching transformRequest
    // (which MapTiler SDK uses internally for its own API key injection).
    // Protocol for the user-selected topo basemap layer (USGS TOPO, ESRI World Topo, etc.).
    // Mirrors imageryAndTopographyOthers in 2D:
    //   - Mapbox layers (id >= 7000): API proxy with auth
    //   - All others (ESRI/USGS ArcGIS servers): direct tile fetch — z/y/x ArcGIS convention
    maptilersdk.addProtocol('user-topo', async (params, abortController) => {
      // URL format: user-topo://{layerId}/{z}/{x}/{y} — layerId makes each layer's
      // tile URLs unique so MapLibre re-requests tiles when switching topo layers.
      const parts = params.url.replace('user-topo://', '').split('/')
      const [z, x, y] = parts.slice(1).map(Number)
      const layer = selectedTopoLayerRef.current
      if (!layer) throw new Error('No topo layer selected')

      let tileUrl, reqHeaders = {}
      if (layer.id >= 7000 && layer.serverLayerName) {
        // Mapbox-proxied layer — use API endpoint with auth (same as 2D)
        const parts = layer.serverLayerName.split('/')
        const layerPath = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`
        tileUrl = `${__API_URI__}/mapbox/render?layer=${layerPath}&zoom=${z}&x=${x}&y=${y}`
        reqHeaders = { Authorization: `Bearer ${tokenRef.current}`, Platform: 'Web', Version: `${__version__}` }
      } else if (layer.server && layer.server !== '<required>') {
        // ArcGIS REST tile servers (ESRI, USGS, etc.) — tile format is z/y/x (y before x)
        tileUrl = `${layer.server}/tile/${z}/${y}/${x}`
      } else if (layer.cloudfrontUrl) {
        // CDN-backed tile URL (same format Render3dLayer uses for data overlay layers)
        tileUrl = layer.cloudfrontUrl.replace('{z}', z).replace('{x}', x).replace('{y}', y)
      } else {
        throw new Error('No tile server for selected topo layer')
      }

      console.log('[user-topo] fetching:', tileUrl)
      const res = await fetch(tileUrl, { headers: reqHeaders, signal: abortController.signal })
      if (!res.ok) { console.error('[user-topo] failed:', res.status, tileUrl); throw new Error(`user-topo tile ${res.status}`) }
      return { data: await res.arrayBuffer() }
    })

    maptilersdk.addProtocol('basemap-topo', async (params, abortController) => {
      const [z, x, y] = params.url.replace('basemap-topo://', '').split('/').map(Number)
      const bbox = tileToBbox(x, y, z)
      // Topo lines ship as two builds: zoomout for z11-16, zoomin for z17+. This
      // handler pinned the zoomin id at every zoom, same as the 2D path did.
      const topoLayerId = topoLinesLayerIdForZoom(z)
      if (topoLayerId === null) throw new Error(`topo lines not served at z${z}`)
      const url = `${__API_URI_3__}/layer/render/${topoLayerId}?zoomLevel=${z}&bbox=${bbox}&width=256&height=256`
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${tokenRef.current}`,
          Platform: 'Web',
          Version: `${__version__}`,
        },
        signal: abortController.signal,
      })
      if (!res.ok) throw new Error(`topo tile ${res.status}`)
      return { data: await res.arrayBuffer() }
    })

    const center = googleMap?.getCenter?.()
    const zoom   = googleMap?.getZoom?.() ?? 4

    const map = new maptilersdk.Map({
      container: mapContainer.current,
      style: BASE_IMAGERY_PROVIDER === 'maptiler'
        ? makeMapTilerSatelliteStyle()
        : makeGoogleSatelliteStyle(__MAPTILER_API_KEY__),
      center: [center?.lng() ?? -100.7122, center?.lat() ?? 41.6171],
      zoom,
      pitch: 0,
      bearing: 2,
      navigationControl: false,
      terrainControl: false,
      geolocateControl: false,
      scaleControl: false,
      fullscreenControl: false,
      fadeDuration: 0,
      maxTileCacheSize: 1000,
    })

    map.on('load', () => {
      try { map.setTerrain({ source: 'terrain-dem', exaggeration: 1 }) } catch (_) {}
      map.setPitch(0)
      map.addControl(new maptilersdk.ScaleControl({ maxWidth: 120, unit: 'imperial' }), 'bottom-right')
      setMapInstance(map)
      setMapReferenceThreeD(map)
      setTimeout(() => { load3dMap?.() }, 5000)
    })

    const limitPitchForZoom = () => {
      const z = map.getZoom()
      const limit = z < 4 ? 0 : z < 6 ? 20 : z < 8 ? 45 : z < 10 ? 60 : 85
      if (map.getPitch() > limit) map.easeTo({ pitch: limit, duration: 300 })
      map.setMaxPitch(limit)
    }
    map.on('zoom', limitPitchForZoom)
    map.on('pitchend', limitPitchForZoom)
    limitPitchForZoom()

    const handleMoveEnd = debounce(() => {
      if (!googleMap) return
      const c = map.getCenter()
      googleMap.setCenter({ lat: c.lat, lng: c.lng })
      googleMap.setZoom(Math.round(map.getZoom()) + 1)
    }, 300)

    map.on('moveend', handleMoveEnd)

    // Allow Shift as an alternative modifier to Ctrl for rotate/pitch gestures.
    // Intercept Shift+mousedown before MapLibre sees it and re-dispatch as
    // Ctrl+mousedown so the DragRotate handler activates for both modifiers.
    const container = map.getContainer()
    const canvas = map.getCanvas()
    const shiftToCtrl = (e) => {
      if (!e.isTrusted || !e.shiftKey || e.ctrlKey) return
      e.stopImmediatePropagation()
      canvas.dispatchEvent(new MouseEvent('mousedown', {
        bubbles: true, cancelable: true, view: window,
        detail: e.detail, screenX: e.screenX, screenY: e.screenY,
        clientX: e.clientX, clientY: e.clientY,
        ctrlKey: true, altKey: e.altKey, shiftKey: false, metaKey: e.metaKey,
        button: e.button, buttons: e.buttons,
      }))
    }
    container.addEventListener('mousedown', shiftToCtrl, true)

    // Right-click drag → zoom in/out (dy up = zoom in, dy down = zoom out)
    // Middle-click drag → tilt (dy) + rotate (dx)
    // stopImmediatePropagation prevents MapLibre's dragRotate on right-click;
    // Ctrl/Shift+drag still rotates via the shiftToCtrl handler above.
    let _rzActive = false, _rzLastY = 0
    let _mcActive = false, _mcLastY = 0, _mcLastX = 0

    const onRightDown = (e) => {
      if (!e.isTrusted || e.button !== 2) return
      e.stopImmediatePropagation()  // prevent MapLibre dragRotate
      _rzActive = true; _rzLastY = e.clientY
    }
    const onMidDown = (e) => {
      if (!e.isTrusted || e.button !== 1) return
      e.stopImmediatePropagation()
      e.preventDefault()             // prevent browser auto-scroll
      _mcActive = true; _mcLastY = e.clientY; _mcLastX = e.clientX
    }
    const onDragMove = (e) => {
      if (_rzActive) {
        const dy = e.clientY - _rzLastY
        _rzLastY = e.clientY
        map.setZoom(map.getZoom() - dy * 0.01)
      }
      if (_mcActive) {
        const dx = e.clientX - _mcLastX
        const dy = e.clientY - _mcLastY
        _mcLastX = e.clientX; _mcLastY = e.clientY
        map.setBearing(map.getBearing() + dx * 0.3)
        map.setPitch(Math.max(0, Math.min(map.getMaxPitch(), map.getPitch() - dy * 0.3)))
      }
    }
    const onDragEnd = () => { _rzActive = false; _mcActive = false }
    const preventCtxMenu = (e) => e.preventDefault()

    container.addEventListener('mousedown', onRightDown, true)
    container.addEventListener('mousedown', onMidDown, true)
    container.addEventListener('contextmenu', preventCtxMenu, true)
    window.addEventListener('mousemove', onDragMove)
    window.addEventListener('mouseup', onDragEnd)

    return () => {
      map.off('moveend', handleMoveEnd)
      container.removeEventListener('mousedown', shiftToCtrl, true)
      container.removeEventListener('mousedown', onRightDown, true)
      container.removeEventListener('mousedown', onMidDown, true)
      container.removeEventListener('contextmenu', preventCtxMenu, true)
      window.removeEventListener('mousemove', onDragMove)
      window.removeEventListener('mouseup', onDragEnd)
      map.remove()
      setMapInstance(null)
      clearSession()
      try { maptilersdk.removeProtocol('google-sat') } catch (_) {}
      try { maptilersdk.removeProtocol('basemap-topo') } catch (_) {}
      try { maptilersdk.removeProtocol('user-topo') } catch (_) {}
    }
  }, [])

  return (
    <>
      <div ref={mapContainer} className="three-d-map">
        {mapInstance && (
          <>
            <Render3dMarkers key={`marker-${styleKey}`} map={mapInstance} />
            <Render3dPhotos map={mapInstance} />
            <Render3dLine key={`line-${styleKey}`} map={mapInstance} />
            <Render3dPolygons key={`poly-${styleKey}`} map={mapInstance} />
            <Render3dTracks key={`track-${styleKey}`} map={mapInstance} />
            <RenderCircles3D key={`circle-${styleKey}`} map={mapInstance} />
            <Render3dLayer key={`layer-${styleKey}`} map={mapInstance} />
            <MapControls3D
              map={mapInstance}
              exaggeration={exaggeration}
              setExaggeration={setExaggeration}
              getPitchLimit={() => {
                const z = mapInstance.getZoom()
                return z < 4 ? 0 : z < 6 ? 20 : z < 8 ? 45 : z < 10 ? 60 : 85
              }}
            />
          </>
        )}
      </div>
      {mapInstance && (
        <div className='threed-basemap-toggle'>
          {BASEMAPS.map(b => (
            <div
              key={b.id}
              onClick={() => handleBasemapChange(b)}
              className={`threed-basemap-btn${activeBasemap === b.id ? ' active' : ''}`}
            >
              {b.label}
            </div>
          ))}
        </div>
      )}
      {mapInstance && (
        <div className='terrain-tools'>
          <TerrainAnalysis map={mapInstance} />
          <Viewshed map={mapInstance} />
        </div>
      )}
      {mapInstance && <CheckWind kind='3d' map={mapInstance} />}
    </>
  )
}

const mapStateToProps = state => ({
  googleMap: typeof state.mapReference === 'function' ? state.mapReference() : null,
  token: state.auth.token,
  selectedTopo: state.userPreferences?.selectedTopo ?? 18,
  layersDictionary: state.layersDictionary,
  userSettings: state.userSettings,
})

const mapDispatchToProps = dispatch => ({
  setMapReferenceThreeD: payload => dispatch(setMapReferenceThreeD(payload)),
  userSettingsSet: settings => dispatch(userSettingsSet(settings)),
  userSettingsUpdateRequest: (id, value) => dispatch(userSettingsUpdateRequest(id, value)),
})

export default connect(mapStateToProps, mapDispatchToProps)(MapTiler3D)
